import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useChat } from './ChatContext';

export default function ConversationList({ onSelect }) {
  const { t } = useTranslation();
  const { conversations, loadingConversations, activeConversationId, newConversation, deleteConversation } = useChat();
  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleNew = () => {
    newConversation();
    onSelect?.();
  };
  const handleSelect = (id) => {
    onSelect?.(id);
  };

  const confirmDelete = () => {
    if (deleteTarget) deleteConversation(deleteTarget);
    setDeleteTarget(null);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ p: 1 }}>
        <Button fullWidth variant="outlined" size="small" startIcon={<PlusOutlined />} onClick={handleNew}>
          {t('chat.newChat')}
        </Button>
      </Box>
      <Divider />
      {!loadingConversations && conversations.length === 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ p: 2, display: 'block', textAlign: 'center' }}>
          {t('chat.noConversations')}
        </Typography>
      )}
      <List dense sx={{ flex: 1, overflowY: 'auto', py: 0 }}>
        {conversations.map((c) => (
          <ListItem key={c.id} disablePadding divider>
            <ListItemButton selected={c.id === activeConversationId} onClick={() => handleSelect(c.id)} sx={{ pr: 5 }}>
              <ListItemText
                primary={c.title}
                primaryTypographyProps={{ noWrap: true, variant: 'body2' }}
                secondary={new Date(c.updatedAt ?? c.createdAt).toLocaleDateString()}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItemButton>
            <ListItemSecondaryAction>
              <Tooltip title={t('common.delete')}>
                <IconButton
                  size="small"
                  edge="end"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteTarget(c.id);
                  }}
                >
                  <DeleteOutlined />
                </IconButton>
              </Tooltip>
            </ListItemSecondaryAction>
          </ListItem>
        ))}
      </List>

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs">
        <DialogTitle>{t('chat.deleteTitle', 'Eliminar conversación')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('chat.deleteConfirm', '¿Estás seguro de que querés eliminar esta conversación?')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>{t('common.cancel', 'Cancelar')}</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
